import React, { useEffect, useState, useRef } from "react";
import CustomSelect from "../../../Common/CustomSelect";
import AuthenticatedImage from "../../../Common/AuthenticatedImage";
import { useKeycloak } from "@react-keycloak/web";
import api from "../../../../api/axios";
import { FaUpload } from "react-icons/fa";
import { FiTrash2, FiX, FiExternalLink } from "react-icons/fi";

import "./ArticleModal.css";

const emptyArticle = {
    name: "",
    tenantId: "",
    ownership: 1,
    manufacturer: "",
    manufacturerShortName: "",
    variant: 1,
    description: "",
    configuratorUrl: "",
    articleNumber: "",
    type: "",
    recordType: "Part",
    partType: "SubGroup",
    size: {
        width: "",
        height: "",
        depth: "",
    },

    xSpacing: {
        left: "",
        right: "",
    },

    ySpacing: {
        top: "",
        bottom: "",
    },

    carrierRail: "",
    deviceTag: "",

    space: null,
    insideClearance: "",
    montageplate: {
        space: null,
        mountingDistance: "",
        size: {
            width: "",
            height: "",
            depth: "",
        }
    },

    images: [],
    cadBlobName: null,
    cadFileName: null,

    attributeRows: [
        {
            key: "",
            type: "string",
            value: ""
        }

    ],

    attributes: {}
};

const PRODUCT_GROUPS = [
    "Terminals",
    "Voltage sources and generators",
    "Protection devices",
    "Measuring instruments",
    "PLC Systems",
    "Power switchgears",
    "Logic items",
    "Relay and Contactors",
    "Converters",
    "Inductors",
    "Ducts",
    "Enclosures",
    "Rails"
];

const CUSTOM_GROUP = "__custom__";

const cadEndpointBase = (partType) => {
    const value = String(partType ?? "").toLowerCase();
    switch (value) {
        case "subgroup":
        case "device":
            return "/subgroup";
        case "enclosure":
            return "/enclosure";
        default:
            return null;
    }
};

const toNumber = (value) => {
    if (value === "" || value === null || value === undefined) return 0;
    const n = Number(value);
    return Number.isNaN(n) ? 0 : n;
};

const normalizeNumbers = (a) => ({
    ...a,
    variant: toNumber(a.variant) || 1,
    size: {
        width: toNumber(a.size?.width),
        height: toNumber(a.size?.height),
        depth: toNumber(a.size?.depth),
    },
    xSpacing: {
        left: toNumber(a.xSpacing?.left),
        right: toNumber(a.xSpacing?.right),
    },
    ySpacing: {
        top: toNumber(a.ySpacing?.top),
        bottom: toNumber(a.ySpacing?.bottom),
    },
    insideClearance: toNumber(a.insideClearance),
    montageplate: {
        ...a.montageplate,
        mountingDistance: toNumber(a.montageplate?.mountingDistance),
        size: {
            width: toNumber(a.montageplate?.size?.width),
            height: toNumber(a.montageplate?.size?.height),
            depth: toNumber(a.montageplate?.size?.depth),
        },
    },
});

const ArticleModal = ({
    isOpen,
    onClose,
    onSave,
    editingArticle,
}) => {
    const [article, setArticle] = useState(emptyArticle);
    const [error, setError] = useState("");
    const [uploadingImage, setUploadingImage] = useState(false);
    const [uploadingCad, setUploadingCad] = useState(false);
    const [isCustomGroup, setIsCustomGroup] = useState(false);
    const imageInputRef = useRef(null);
    const cadInputRef = useRef(null);
    const { keycloak } = useKeycloak();

    const tenantId =
        localStorage.getItem("tenant") ||
        keycloak?.tokenParsed?.groups?.[0] ||
        "";

    const articleId = article.id;
    const cadBase = cadEndpointBase(article.partType);

    useEffect(() => {
        if (isOpen && editingArticle) {

            const attributeRows = Object.entries(
                editingArticle.attributes ?? {}
            ).map(([key, value]) => ({
                key,
                value: String(value),
                type:
                    typeof value === "boolean"
                        ? "boolean"
                        : Number.isInteger(value)
                            ? "integer"
                            : typeof value === "number"
                                ? "float"
                                : "string"
            }));

            setIsCustomGroup(
                !!editingArticle.name &&
                !PRODUCT_GROUPS.includes(editingArticle.name)
            );

            setArticle({
                ...emptyArticle,
                ...editingArticle,

                attributeRows,

                partType:
                    editingArticle.partType ??
                    emptyArticle.partType,

                tenantId:
                    editingArticle.tenantId ??
                    tenantId,

                size:
                    editingArticle.size ??
                    emptyArticle.size,

                xSpacing:
                    editingArticle.xSpacing ??
                    emptyArticle.xSpacing,

                ySpacing:
                    editingArticle.ySpacing ??
                    emptyArticle.ySpacing,

                insideClearance:
                    editingArticle.insideClearance ??
                    emptyArticle.insideClearance,

                configuratorUrl:
                    editingArticle.configuratorUrl ?? "",

                montageplate: {
                    ...emptyArticle.montageplate,
                    ...editingArticle.montageplate,
                    size: {
                        ...emptyArticle.montageplate.size,
                        ...editingArticle.montageplate?.size,
                    }
                },

                images:
                    editingArticle.images ??
                    emptyArticle.images,
            });

            setError("");

        } else if (isOpen) {

            setIsCustomGroup(false);

            setArticle({
                ...emptyArticle,
                tenantId,
            });

            setError("");
        }
    }, [isOpen, editingArticle, tenantId]);

    const handleChange = (field, value) => {
        setArticle((prev) => ({ ...prev, [field]: value }));
    };

    const handleNestedChange = (group, field, value) => {
        setArticle((prev) => ({
            ...prev,
            [group]: {
                ...prev[group],
                [field]: value,
            },
        }));
    };

    const handleGroupChange = (value) => {
        if (value === CUSTOM_GROUP) {
            setIsCustomGroup(true);
            handleChange("name", "");
        } else {
            setIsCustomGroup(false);
            handleChange("name", value);
        }
    };

    const handleSubmit = async () => {
        if (!article.name.trim()) {
            setError("Productgroup is required.");
            return;
        }

        const attributes = {};

        article.attributeRows.forEach(attr => {
            if (!attr.key) return;

            let value;
            switch (attr.type) {
                case "integer":
                    value = parseInt(attr.value, 10) || 0;
                    break;
                case "float":
                    value = parseFloat(attr.value) || 0;
                    break;
                case "boolean":
                    value = attr.value === "true";
                    break;
                default:
                    value = attr.value;
                    break;
            }

            attributes[attr.key] = value;
        });

        const configurator = checkConfiguratorUrl(article.configuratorUrl);
        if (!configurator.ok) {
            setError("Configurator link must be a full address starting with http:// or https://");
            return;
        }

        setError("");

        const wasExisting = !!article.id;
        const payload = normalizeNumbers({
            ...article,
            attributes,
            configuratorUrl: configurator.value,   // "" -> null = no button in the tree
        });
        try {
            const saved = await onSave(payload);

            if (wasExisting) {
                onClose();
                return;
            }

            if (saved?.id) {
                setArticle((prev) => ({
                    ...prev,
                    ...payload,
                    ...saved,
                    partType: saved.partType ?? prev.partType,
                    attributeRows: prev.attributeRows,
                }));
            } else {
                // Still show the normalized values (0 instead of empty)
                setArticle((prev) => ({
                    ...prev,
                    ...payload,
                    attributeRows: prev.attributeRows,
                }));
            }
        } catch (err) {
            setError("Failed to save article.");
        }
    };

    const addAttribute = () => {
        setArticle(prev => ({
            ...prev,
            attributeRows: [
                ...prev.attributeRows,
                {
                    key: "",
                    type: "string",
                    value: ""
                }
            ]
        }));
    };

    const removeAttribute = (index) => {
        setArticle(prev => ({
            ...prev,
            attributeRows: prev.attributeRows.filter(
                (_, i) => i !== index
            )
        }));
    };

    const updateAttribute = (index, field, value) => {
        setArticle(prev => ({
            ...prev,
            attributeRows: prev.attributeRows.map((attr, i) =>
                i === index
                    ? {
                        ...attr,
                        [field]: value
                    }
                    : attr
            )
        }));
    };

    // ---- Images ----

    const handleImageButtonClick = () => {
        if (!articleId) return;
        imageInputRef.current?.click();
    };

    const handleImageFileSelected = async (e) => {
        const file = e.target.files[0];
        if (!file || !articleId) return;

        setUploadingImage(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const { data } = await api.post(
                `/article/${articleId}/images`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            setArticle((prev) => ({
                ...prev,
                images: [...(prev.images ?? []), data.blobName],
            }));
        } catch (err) {
            console.error("Image upload failed", err);
            setError("Image upload failed.");
        } finally {
            setUploadingImage(false);
            e.target.value = "";
        }
    };

    const handleImageDelete = async (blobName) => {
        if (!articleId) return;

        try {
            await api.delete(`/article/${articleId}/images`, {
                params: { blobName },
            });

            setArticle((prev) => ({
                ...prev,
                images: (prev.images ?? []).filter((i) => i !== blobName),
            }));
        } catch (err) {
            console.error("Image delete failed", err);
            setError("Failed to remove image.");
        }
    };

    // ---- CAD ----

    const handleCadButtonClick = () => {
        if (!articleId || !cadBase) return;
        cadInputRef.current?.click();
    };

    const handleCadFileSelected = async (e) => {
        const file = e.target.files[0];
        if (!file || !articleId || !cadBase) return;

        setUploadingCad(true);

        try {
            const formData = new FormData();
            formData.append("file", file);

            const { data } = await api.post(
                `${cadBase}/${articleId}/cad`,
                formData,
                { headers: { "Content-Type": "multipart/form-data" } }
            );

            setArticle((prev) => ({
                ...prev,
                cadFileName: data.fileName,
                cadBlobName: data.blobName,
            }));
        } catch (err) {
            console.error("CAD upload failed", err);
            setError("CAD upload failed.");
        } finally {
            setUploadingCad(false);
            e.target.value = "";
        }
    };

    const handleCadDelete = async () => {
        if (!articleId || !cadBase) return;

        try {
            await api.delete(`${cadBase}/${articleId}/cad`);

            setArticle((prev) => ({
                ...prev,
                cadFileName: null,
                cadBlobName: null,
            }));
        } catch (err) {
            console.error("CAD delete failed", err);
            setError("Failed to remove CAD file.");
        }
    };

    // Same rule as the backend (ConfiguratorUrlValidator): empty = no configurator,
    // otherwise an absolute http(s) link.
    const checkConfiguratorUrl = (value) => {
        const v = (value ?? "").trim();
        if (!v) return { ok: true, value: null };
        try {
            const u = new URL(v);
            if (u.protocol === "http:" || u.protocol === "https:") return { ok: true, value: v };
        } catch {
            // not a URL
        }
        return { ok: false, value: v };
    };

    if (!isOpen) return null;

    return (
        <div className="modal-backdrop-article">
            <div className="modal-content-article">
                <div className="modal-header">
                    <h2 className="modal-title">
                        {editingArticle ? "Edit Article" : "Add Article"}
                    </h2>

                    <button
                        type="button"
                        className="modal-close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                        <FiX />
                    </button>
                </div>

                <div className="modal-body">
                    <div className="modal-grid">

                        <div className="modal-field">
                            <label>Record Type</label>
                            <select
                                className="modal-select"
                                value={article.recordType}
                                onChange={(e) =>
                                    handleChange("recordType", e.target.value)
                                }
                            >
                                <option value="Part">Part</option>
                                <option value="Assembly">Assembly</option>
                                <option value="Module">Module</option>
                            </select>
                        </div>

                        {article.recordType === "Part" && (
                            <div className="modal-field">
                                <label>Part Type</label>
                                <select
                                    className="modal-select"
                                    value={article.partType || "SubGroup"}
                                    onChange={(e) =>
                                        handleChange("partType", e.target.value)
                                    }
                                >
                                    <option value="SubGroup">Device</option>
                                    <option value="Enclosure">Enclosure</option>
                                    <option value="Rail">Rail</option>
                                    <option value="Duct">Duct</option>
                                </select>
                            </div>
                        )}

                        <div className="modal-field">
                            <label>Tenant</label>
                            <input
                                className="modal-input tenant-input"
                                placeholder="Tenant"
                                value={article.tenantId}
                                readOnly
                            />
                        </div>

                        <div className="modal-field">
                            <label>Productgroup</label>
                            <select
                                className="modal-select"
                                value={isCustomGroup ? CUSTOM_GROUP : article.name}
                                onChange={(e) => handleGroupChange(e.target.value)}
                            >
                                <option value="" disabled>
                                    Select productgroup
                                </option>
                                {PRODUCT_GROUPS.map((group) => (
                                    <option key={group} value={group}>
                                        {group}
                                    </option>
                                ))}
                                <option value={CUSTOM_GROUP}>Custom...</option>
                            </select>

                            {isCustomGroup && (
                                <input
                                    className="modal-input"
                                    style={{ marginTop: 8 }}
                                    placeholder="Enter custom productgroup"
                                    value={article.name}
                                    onChange={(e) =>
                                        handleChange("name", e.target.value)
                                    }
                                    autoFocus
                                />
                            )}
                        </div>

                        <div className="modal-field">
                            <label>Manufacturer</label>
                            <input
                                className="modal-input"
                                placeholder="Manufacturer"
                                value={article.manufacturer}
                                onChange={(e) =>
                                    handleChange("manufacturer", e.target.value)
                                }
                            />
                        </div>

                        <div className="modal-field">
                            <label>Manufacturer Short Name</label>
                            <input
                                className="modal-input"
                                placeholder="Manufacturer Short Name"
                                value={article.manufacturerShortName}
                                onChange={(e) =>
                                    handleChange("manufacturerShortName", e.target.value.toUpperCase())
                                }
                                maxLength={3}
                            />
                        </div>

                        <div className="modal-field">
                            <label>Type</label>
                            <input
                                className="modal-input"
                                placeholder="Type"
                                value={article.type}
                                onChange={(e) =>
                                    handleChange("type", e.target.value)
                                }
                            />
                        </div>

                        <div className="modal-field">
                            <label>Article Number</label>
                            <input
                                className="modal-input"
                                placeholder="Article Number"
                                value={article.articleNumber}
                                onChange={(e) =>
                                    handleChange("articleNumber", e.target.value)
                                }
                            />
                        </div>

                        <div className="modal-field">
                            <label>Variant</label>
                            <input
                                className="modal-input"
                                type="number"
                                placeholder="Variant"
                                value={article.variant}
                                onChange={(e) =>
                                    handleChange("variant", Number(e.target.value))
                                }
                                min={1}
                            />
                        </div>

                        <div className="modal-field col-span-2">
                            <label>Description</label>
                            <textarea
                                className="modal-input modal-textarea"
                                placeholder="Description"
                                rows={4}
                                value={article.description ?? ""}
                                onChange={(e) =>
                                    handleChange("description", e.target.value)
                                }
                            />
                        </div>

                        <div className="modal-field col-span-2">
                            <label>External Link</label>
                            <div className="modal-url-row">
                                <input
                                    className="modal-input"
                                    type="url"
                                    inputMode="url"
                                    placeholder="https://… (optional)"
                                    value={article.configuratorUrl ?? ""}
                                    onChange={(e) =>
                                        handleChange("configuratorUrl", e.target.value)
                                    }
                                />
                                {(() => {
                                    const c = checkConfiguratorUrl(article.configuratorUrl);
                                    return c.ok && c.value ? (
                                        <a
                                            className="btn-secondary2 modal-url-test"
                                            href={c.value}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Open the link to test it"
                                        >
                                            <FiExternalLink />
                                        </a>
                                    ) : null;
                                })()}
                            </div>
                        </div>

                        <h4 className="modal-section-title">
                            Dimensions
                        </h4>


                        <div className="modal-field">
                            <label>Width</label>
                            <input
                                className="modal-input"
                                type="number"
                                placeholder="Width"
                                value={article.size.width ?? ""}
                                onChange={(e) =>
                                    handleNestedChange(
                                        "size",
                                        "width",
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </div>

                        <div className="modal-field">
                            <label>Height</label>
                            <input
                                className="modal-input"
                                type="number"
                                placeholder="Height"
                                value={article.size.height ?? ""}
                                onChange={(e) =>
                                    handleNestedChange(
                                        "size",
                                        "height",
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </div>

                        <div className="modal-field">
                            <label>Depth</label>
                            <input
                                className="modal-input"
                                type="number"
                                placeholder="Depth"
                                value={article.size.depth ?? ""}
                                onChange={(e) =>
                                    handleNestedChange(
                                        "size",
                                        "depth",
                                        Number(e.target.value)
                                    )
                                }
                            />
                        </div>

                        {article.recordType === "Part" && article.partType === "SubGroup" && (
                            <>

                                <h4 className="modal-section-title">
                                    Device Settings
                                </h4>

                                <div className="modal-field">
                                    <label>Device Tag</label>
                                    <input
                                        className="modal-input"
                                        placeholder="Device Tag"
                                        value={article.deviceTag}
                                        onChange={(e) =>
                                            handleChange("deviceTag", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Carrier Rail</label>
                                    <input
                                        className="modal-input"
                                        placeholder="Carrier Rail"
                                        value={article.carrierRail}
                                        onChange={(e) =>
                                            handleChange("carrierRail", e.target.value)
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Left Spacing</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Left Spacing"
                                        value={article.xSpacing.left ?? ""}
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "xSpacing",
                                                "left",
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Right Spacing</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Right Spacing"
                                        value={article.xSpacing.right ?? ""}
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "xSpacing",
                                                "right",
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Top Spacing</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Top Spacing"
                                        value={article.ySpacing.top ?? ""}
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "ySpacing",
                                                "top",
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Bottom Spacing</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Bottom Spacing"
                                        value={article.ySpacing.bottom ?? ""}
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "ySpacing",
                                                "bottom",
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>
                            </>
                        )}

                        {article.recordType === "Part" && article.partType === "Enclosure" && (
                            <>
                                <h4 className="modal-section-title">Enclosure Settings</h4>

                                <div className="modal-field">
                                    <label>Cabinet Inside Clearance</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Cabinet Inside Clearance"
                                        value={article.insideClearance ?? ""}
                                        onChange={(e) =>
                                            handleChange("insideClearance", Number(e.target.value))
                                        }
                                    />
                                </div>

                                <h4 className="modal-section-title">Montage Plate</h4>

                                <div className="modal-field">
                                    <label>Mounting Distance</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Mounting Distance"
                                        value={article.montageplate.mountingDistance ?? ""}
                                        onChange={(e) =>
                                            handleNestedChange(
                                                "montageplate",
                                                "mountingDistance",
                                                Number(e.target.value)
                                            )
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Plate Width</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Plate Width"
                                        value={article.montageplate.size.width ?? ""}
                                        onChange={(e) =>
                                            setArticle(prev => ({
                                                ...prev,
                                                montageplate: {
                                                    ...prev.montageplate,
                                                    size: {
                                                        ...prev.montageplate.size,
                                                        width: Number(e.target.value)
                                                    }
                                                }
                                            }))
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Plate Height</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Plate Height"
                                        value={article.montageplate.size.height ?? ""}
                                        onChange={(e) =>
                                            setArticle(prev => ({
                                                ...prev,
                                                montageplate: {
                                                    ...prev.montageplate,
                                                    size: {
                                                        ...prev.montageplate.size,
                                                        height: Number(e.target.value)
                                                    }
                                                }
                                            }))
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Plate Depth</label>
                                    <input
                                        className="modal-input"
                                        type="number"
                                        placeholder="Plate Depth"
                                        value={article.montageplate.size.depth ?? ""}
                                        onChange={(e) =>
                                            setArticle(prev => ({
                                                ...prev,
                                                montageplate: {
                                                    ...prev.montageplate,
                                                    size: {
                                                        ...prev.montageplate.size,
                                                        depth: Number(e.target.value)
                                                    }
                                                }
                                            }))
                                        }
                                    />
                                </div>
                            </>
                        )}

                        <h4 className="modal-section-title col-span-2">
                            Images
                        </h4>

                        <div className="modal-image-section col-span-2">
                            {!articleId && (
                                <p className="modal-hint">
                                    Save the article first to attach images.
                                </p>
                            )}

                            <div className="modal-image-list">
                                {(article.images ?? []).map((img) => (
                                    <div key={img} className="modal-image-thumb">
                                        <AuthenticatedImage articleId={articleId} blobName={img} />
                                        <button
                                            type="button"
                                            className="modal-image-remove"
                                            onClick={() => handleImageDelete(img)}
                                        >
                                            <FiX />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleImageButtonClick}
                                disabled={!articleId || uploadingImage}
                            >
                                <FaUpload /> {uploadingImage ? "Uploading..." : "Add Image"}
                            </button>

                            <input
                                ref={imageInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleImageFileSelected}
                            />
                        </div>

                        <h4 className="modal-section-title col-span-2">
                            CAD File
                        </h4>

                        <div className="modal-cad-section col-span-2">
                            {!articleId && (
                                <p className="modal-hint">
                                    Save the article first to attach a CAD file.
                                </p>
                            )}

                            {articleId && !cadBase && (
                                <p className="modal-hint">
                                    CAD upload isn't available for this part type yet.
                                </p>
                            )}

                            {article.cadFileName && (
                                <div className="modal-cad-current">
                                    <span>{article.cadFileName}</span>
                                    <button
                                        type="button"
                                        className="modal-cad-remove"
                                        onClick={handleCadDelete}
                                    >
                                        <FiTrash2 />
                                    </button>
                                </div>
                            )}

                            <button
                                type="button"
                                className="btn-primary"
                                onClick={handleCadButtonClick}
                                disabled={!articleId || !cadBase || uploadingCad}
                            >
                                <FaUpload />{" "}
                                {uploadingCad
                                    ? "Uploading..."
                                    : article.cadFileName
                                        ? "Replace CAD File"
                                        : "Upload CAD File"}
                            </button>

                            <input
                                ref={cadInputRef}
                                type="file"
                                accept=".step,.stp,.dwg,.dxf,.iges,.igs,.ema,.E3d"
                                style={{ display: "none" }}
                                onChange={handleCadFileSelected}
                            />
                        </div>

                        <h4 className="modal-section-title">
                            Attributes
                        </h4>

                        {(article.attributeRows ?? []).map((attr, index) => (
                            <React.Fragment key={index}>

                                <div className="modal-field">
                                    <label>Attribute Key</label>
                                    <input
                                        className="modal-input"
                                        placeholder="Attribute Key"
                                        value={attr.key}
                                        onChange={(e) =>
                                            updateAttribute(
                                                index,
                                                "key",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Type</label>
                                    <CustomSelect
                                        className="modal-select"
                                        value={attr.type}
                                        onChange={(val) =>
                                            updateAttribute(index, "type", val)
                                        }
                                        options={[
                                            { value: "string", label: "String" },
                                            { value: "integer", label: "Integer" },
                                            { value: "float", label: "Float" },
                                            { value: "boolean", label: "Boolean" },
                                        ]}
                                    />
                                </div>

                                <div className="modal-field">
                                    <label>Value</label>
                                    <input
                                        className="modal-input"
                                        placeholder="Value"
                                        value={attr.value}
                                        onChange={(e) =>
                                            updateAttribute(
                                                index,
                                                "value",
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>

                                <button
                                    type="button"
                                    className="btn-secondary2"
                                    onClick={() => removeAttribute(index)}
                                >
                                    Remove
                                </button>

                            </React.Fragment>
                        ))}

                        <button
                            type="button"
                            className="btn-primary col-span-2"
                            onClick={addAttribute}
                        >
                            Add Attribute
                        </button>
                    </div>

                    {error && (
                        <div className="modal-error">
                            {error}
                        </div>
                    )}
                </div>

                <div className="modal-actions">
                    <button className="btn-primary" onClick={handleSubmit}>
                        {article.id ? "Update" : "Add"}
                    </button>

                    <button
                        className="btn-secondary2"
                        onClick={onClose}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ArticleModal;
